import { Result, Button, Tag, Alert } from 'antd';
import { ArrowRightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getSignupState, clearSignupState } from '../../contexts/signupStore';
import { formatDate, paiseToRupees } from '../../utils/format';

export default function SignupDonePage() {
  const navigate = useNavigate();
  const state = getSignupState();
  const provisioned = state.provisioned;

  // Stage 5K-CustomPlan: every public signup now ends in pending_approval.
  // Backend sets accountStatus + approvalRequired on the verifyPayment /
  // recoverPayment response; the frontend treats them as the source of
  // truth. We default to pending_approval if the field is missing so
  // older response shapes still produce a safe message rather than
  // implying immediate access.
  const accountStatus =
    (provisioned && provisioned.accountStatus) || 'pending_approval';
  const isPending = accountStatus === 'pending_approval';

  // Plan-type flags. The backend response carries planType/isCustomPlan/
  // isTrial; we derive these for display only — no business logic flows
  // off them.
  const isCustomPlan = Boolean(
    provisioned && (provisioned.isCustomPlan || provisioned.isCoupon)
  );
  const isTrial = Boolean(provisioned && provisioned.isTrial);
  const customCode = provisioned
    ? provisioned.customCode || provisioned.couponCode
    : null;
  const showPlanRows = isTrial || isCustomPlan;

  function goLogin() {
    clearSignupState();
    navigate('/login', { replace: true });
  }

  return (
    <div className="gi-onboarding-rise bg-white rounded-2xl shadow-auth border border-surface-200 p-8 sm:p-10">
      <Result
        status={isPending ? 'info' : 'success'}
        icon={
          isPending ? (
            <ClockCircleOutlined className="!text-amber-500" />
          ) : undefined
        }
        title={
          <span className="text-2xl font-bold text-ink-900">
            {isPending ? 'Payment received' : 'Account created successfully'}
          </span>
        }
        subTitle={
          <span className="text-ink-500">
            {isPending
              ? 'Your account has been created and is pending approval.'
              : 'Your account is ready. You can now log in with your master credentials.'}
          </span>
        }
      />

      {isPending ? (
        <Alert
          type="warning"
          showIcon
          className="!mb-5"
          message="Account pending approval"
          description="Our team will review and activate your account shortly. You will be able to log in after approval."
        />
      ) : null}

      {provisioned ? (
        <div className="rounded-xl border border-surface-200 bg-surface-50 overflow-hidden mb-5">
          <div className="px-5 py-3 bg-white border-b border-surface-200">
            <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0">
              Your account
            </p>
          </div>
          <div className="divide-y divide-surface-200">
            <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
              <span className="text-ink-600 shrink-0">Account id</span>
              <code className="text-xs text-ink-700 truncate">{provisioned.tenantId}</code>
            </div>
            <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
              <span className="text-ink-600 shrink-0">Account code</span>
              <Tag>{provisioned.tenantSlug}</Tag>
            </div>
            <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
              <span className="text-ink-600 shrink-0">Master email (login)</span>
              <span className="font-medium text-ink-900 truncate">
                {provisioned.masterEmail}
              </span>
            </div>
            {/* Plan-specific rows. Plan / Custom code / Trial-or-Plan-ends-on
                / Included-users / Paid-amount appear when the backend
                sent isTrial=true or isCustomPlan=true. The standard paid
                path keeps the Subscription tag below as before. */}
            {showPlanRows ? (
              <>
                <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
                  <span className="text-ink-600 shrink-0">Plan</span>
                  <Tag color="green">
                    {provisioned.planName ||
                      (isTrial ? 'Trial Plan' : 'Custom Plan')}
                  </Tag>
                </div>
                {isCustomPlan && customCode ? (
                  <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
                    <span className="text-ink-600 shrink-0">Custom code</span>
                    <code className="text-xs font-semibold text-violet-700">
                      {customCode}
                    </code>
                  </div>
                ) : null}
                {provisioned.trialEndsAt ? (
                  <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
                    <span className="text-ink-600 shrink-0">Trial ends on</span>
                    <span className="font-medium text-ink-900">
                      {formatDate(provisioned.trialEndsAt)}
                    </span>
                  </div>
                ) : null}
                {!provisioned.trialEndsAt && provisioned.planEndsAt ? (
                  <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
                    <span className="text-ink-600 shrink-0">Plan ends on</span>
                    <span className="font-medium text-ink-900">
                      {formatDate(provisioned.planEndsAt)}
                    </span>
                  </div>
                ) : null}
                {provisioned.includedUsers ? (
                  <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
                    <span className="text-ink-600 shrink-0">Included users</span>
                    <span className="font-medium text-ink-900">
                      {provisioned.includedUsers} user
                      {provisioned.includedUsers === 1 ? '' : 's'}
                    </span>
                  </div>
                ) : null}
                {provisioned.paidAmountPaise ? (
                  <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
                    <span className="text-ink-600 shrink-0">Paid amount</span>
                    <span className="font-medium text-ink-900 tabular-nums whitespace-nowrap">
                      {paiseToRupees(provisioned.paidAmountPaise)}
                    </span>
                  </div>
                ) : null}
              </>
            ) : null}
            <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
              <span className="text-ink-600 shrink-0">Subscription</span>
              <Tag color="green">{provisioned.subscriptionStatus}</Tag>
            </div>
            {/* Stage 5K-CustomPlan: account access status (separate from
                billing/subscription status). Pending approval renders an
                amber tag; once approved this row will display green. */}
            <div className="flex items-center justify-between px-5 py-3 text-sm gap-3">
              <span className="text-ink-600 shrink-0">Account status</span>
              <Tag color={isPending ? 'gold' : 'green'}>
                {isPending ? 'Pending approval' : 'Active'}
              </Tag>
            </div>
          </div>
        </div>
      ) : null}

      <p className="text-sm text-ink-500 mb-5 text-center">
        {isPending
          ? 'Use the email above + the password you just chose to log in once your account is approved.'
          : 'Use the email above + the password you just chose to log in.'}
      </p>

      <div className="flex justify-center">
        <Button
          type="primary"
          size="large"
          onClick={goLogin}
          icon={<ArrowRightOutlined />}
          iconPosition="end"
          className="!h-11 !px-6 !text-base !font-semibold"
        >
          Go to login
        </Button>
      </div>
    </div>
  );
}
